import { jsxs, jsx } from "react/jsx-runtime";
import { Actions, Action } from "../../ai-elements/actions.js";
import { Shimmer } from "../../ai-elements/shimmer.js";
import brandclub_colors from "../../colors/brandclubColors.js";
import { CopyIcon } from "../../icons/CopyIcon.js";
import { ShareIcon } from "../../icons/ShareIcon.js";
import { ThumbsDownIcon } from "../../icons/ThumbsDownIcon.js";
import { ThumbsUpIcon } from "../../icons/ThumbsUpIcon.js";
import React__default, { useState, useMemo, useRef, useLayoutEffect } from "react";
import { toast } from "sonner";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { sendMessageFeedback } from "../api.js";
import { marked } from "marked";
import { ConciergeMessagePart } from "./ConciergeMessagePart/index.js";
import DevInfoPanel from "./DevInfoPanel.js";
import { StoreLocatorWidget } from "./StoreLocatorWidget/index.js";
import { OrderCardList } from "./OrderCard.js";
import { ChainOfThoughtWidget, MessageBubbleWidget, themeColor, customColor, FollowUpQueriesWidget } from "@brandclub/bc-widgets";
const RetryIcon = () => /* @__PURE__ */ jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "15", height: "15", viewBox: "0 0 15 15", fill: "none", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M0.782516 13.998V10.665C0.782516 10.2978 1.08029 10 1.44755 10H4.78056L4.91435 10.0137C5.21743 10.0756 5.4456 10.3436 5.4456 10.665C5.4456 10.9864 5.21743 11.2544 4.91435 11.3164L4.78056 11.3301H2.75517C3.86734 12.5566 5.49894 13.333 7.28056 13.333C10.3405 13.333 12.8664 11.0427 13.2356 8.08301L13.2659 7.95117C13.365 7.65833 13.6591 7.46515 13.9778 7.50488C14.3422 7.55032 14.6003 7.8827 14.555 8.24707L14.5052 8.58398C13.9106 12.0368 10.9029 14.663 7.28056 14.6631C5.29155 14.6631 3.45239 13.87 2.11259 12.5791V13.998C2.11259 14.3652 1.81462 14.6629 1.44755 14.6631C1.08029 14.6631 0.782516 14.3653 0.782516 13.998ZM1.32548 6.58105C1.27988 6.94523 0.947529 7.20349 0.583297 7.1582C0.21885 7.11276 -0.0402741 6.78046 0.00517177 6.41602L1.32548 6.58105ZM7.28056 0C9.27443 3.88739e-05 11.1177 0.797213 12.4583 2.09375V0.665039C12.4583 0.297863 12.7562 0.000151989 13.1233 0C13.4906 0 13.7884 0.29777 13.7884 0.665039V3.99805C13.7884 4.36532 13.4906 4.66309 13.1233 4.66309H9.79033C9.42306 4.66309 9.12529 4.36532 9.12529 3.99805C9.12546 3.63093 9.42317 3.33301 9.79033 3.33301H11.806C10.6938 2.10658 9.0621 1.33012 7.28056 1.33008C4.2205 1.33008 1.69458 3.62118 1.32548 6.58105L0.665328 6.49805L0.00517177 6.41602C0.45626 2.79872 3.54127 0 7.28056 0Z",
    fill: "currentColor"
  }
) });
const ChatMessageComponent = React__default.forwardRef(
  ({
    role,
    content,
    bottomRef,
    isTyping = false,
    isStreaming = false,
    isLastMessage = false,
    messageIndex,
    conversationId,
    feedback,
    followUpQueries = [],
    nextUserMessage,
    storeLocator,
    streamingStoreLocator,
    orders,
    devInfo,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView,
    uiMessageParts,
    transientStatus,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    followUpsLoading,
    onRetry,
    onOpenShareDialog
  }, _ref) => {
    var _a;
    const isUser = role === "user";
    const hasUiMessageParts = Array.isArray(uiMessageParts) && uiMessageParts.length > 0;
    const hasAnyContent = content.length > 0 || hasUiMessageParts && ((uiMessageParts == null ? void 0 : uiMessageParts.some((p) => p.type !== "text" || p.text)) ?? false);
    const shimmerActive = !isTyping && isStreaming && !!transientStatus && content.length === 0;
    const [liked, setLiked] = useState((feedback == null ? void 0 : feedback.type) === "like");
    const [disliked, setDisliked] = useState((feedback == null ? void 0 : feedback.type) === "dislike");
    const [copied, setCopied] = useState(false);
    const [localSelectedFollowUp, setLocalSelectedFollowUp] = useState(void 0);
    const derivedSelectedFollowUp = useMemo(() => {
      if (!nextUserMessage || !followUpQueries.length) return void 0;
      return followUpQueries.find((query) => query === nextUserMessage);
    }, [nextUserMessage, followUpQueries]);
    const selectedFollowUp = derivedSelectedFollowUp || localSelectedFollowUp;
    const userMessageRef = useRef(null);
    const [isMultiline, setIsMultiline] = useState(false);
    const assistantContentRef = useRef(null);
    const [assistantBubbleWidth, setAssistantBubbleWidth] = useState(void 0);
    const hasWidgetParts = useMemo(
      () => Array.isArray(uiMessageParts) && uiMessageParts.some(
        (p) => !["text", "source-url", "data-follow-ups", "tool-search_products", "tool-analyze_query"].includes(p.type)
      ),
      [uiMessageParts]
    );
    useLayoutEffect(() => {
      if (!isUser || !userMessageRef.current) return;
      const el = userMessageRef.current;
      const textNode = el.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const rects = range.getClientRects();
        setIsMultiline(rects.length > 1);
      } else {
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
        setIsMultiline(el.scrollHeight > lineHeight * 1.5);
      }
    }, [isUser, content]);
    useLayoutEffect(() => {
      if (isUser || isTyping || hasWidgetParts) {
        setAssistantBubbleWidth(void 0);
        return;
      }
      const el = assistantContentRef.current;
      if (!el) {
        setAssistantBubbleWidth(void 0);
        return;
      }
      const containerLeft = el.getBoundingClientRect().left;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let maxRight = 0;
      let node;
      while (node = walker.nextNode()) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const rects = range.getClientRects();
        for (let i = 0; i < rects.length; i++) {
          maxRight = Math.max(maxRight, rects[i].right);
        }
      }
      if (maxRight > 0) {
        const textWidth = maxRight - containerLeft;
        const computedStyle = window.getComputedStyle(el);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        const neededWidth = Math.ceil(textWidth) + paddingLeft + paddingRight + 2;
        if (neededWidth < el.offsetWidth - 8) {
          setAssistantBubbleWidth(neededWidth);
        } else {
          setAssistantBubbleWidth(void 0);
        }
      } else {
        setAssistantBubbleWidth(void 0);
      }
    }, [isUser, isTyping, content, hasUiMessageParts, hasWidgetParts]);
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (error) {
        toast.error("Failed to copy message");
      }
    };
    const handleShare = () => {
      if (!conversationId) {
        console.warn("Cannot share: missing conversationId");
        return;
      }
      onOpenShareDialog == null ? void 0 : onOpenShareDialog(conversationId, messageIndex);
    };
    const handleLike = async () => {
      var _a2;
      if (!conversationId || messageIndex === void 0) return;
      const newState = !liked;
      setLiked(newState);
      setDisliked(false);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "like" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        if (onUpdateFeedback && ((_a2 = result.message.metadata) == null ? void 0 : _a2.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        setLiked(!newState);
      }
    };
    const handleDislike = async () => {
      var _a2;
      if (!conversationId || messageIndex === void 0) return;
      const newState = !disliked;
      setDisliked(newState);
      setLiked(false);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "dislike" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        if (onUpdateFeedback && ((_a2 = result.message.metadata) == null ? void 0 : _a2.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        setDisliked(!newState);
      }
    };
    const handleFollowUpClick = (query) => {
      if (!isLastMessage || selectedFollowUp) return;
      setLocalSelectedFollowUp(query);
      onSearch == null ? void 0 : onSearch(query);
    };
    const getJustifyContent = (alignment) => {
      switch (alignment) {
        case "right":
          return "flex-end";
        case "center":
          return "center";
        default:
          return "flex-start";
      }
    };
    const renderUserMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-user-message",
        displayName: "User Message Bubble",
        initialState: {
          bgColor: customColor(brandclub_colors.blueWhite),
          textColor: themeColor("textPrimary"),
          maxWidth: "90%",
          alignment: "right"
        },
        children: (styles, resolveColor) => {
          const borderRadius = isMultiline ? styles.borderRadius : styles.borderRadiusShort;
          const fontFamily = styles.fontFamilyCss ? styles.fontFamilyCss !== "inherit" ? styles.fontFamilyCss : "var(--concierge-user-message-font-family, inherit)" : "var(--concierge-user-message-font-family, inherit)";
          return /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: getJustifyContent(styles.alignment),
                width: "100%"
              },
              children: /* @__PURE__ */ jsx("div", { style: { maxWidth: styles.maxWidth }, children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "message-content user-message",
                  style: {
                    maxWidth: "100%",
                    minWidth: "50px",
                    backgroundColor: resolveColor(styles.bgColor),
                    color: resolveColor(styles.textColor),
                    borderRadius: `${borderRadius}px`,
                    wordWrap: "break-word",
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    fontSize: `${styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    fontFamily,
                    lineHeight: styles.lineHeight,
                    ...!isMultiline && styles.singleLineHeight > 0 ? {
                      minHeight: styles.singleLineHeight,
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box"
                    } : {}
                  },
                  children: /* @__PURE__ */ jsx("div", { ref: userMessageRef, style: { whiteSpace: "pre-wrap" }, children: content })
                }
              ) })
            }
          );
        }
      }
    );
    const renderAssistantMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-assistant-message",
        displayName: "Assistant Message Bubble",
        initialState: {
          bgColor: themeColor("secondary"),
          textColor: themeColor("textPrimary"),
          maxWidth: "100%",
          paddingX: 0,
          paddingY: 0,
          alignment: "left"
        },
        children: (styles, resolveColor) => /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: getJustifyContent(styles.alignment),
              width: "100%"
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  maxWidth: styles.maxWidth,
                  width: hasWidgetParts ? "100%" : assistantBubbleWidth ?? void 0,
                  color: resolveColor(styles.textColor)
                },
                children: [
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      ref: assistantContentRef,
                      className: "message-content",
                      style: {
                        maxWidth: "100%",
                        minWidth: "50px",
                        backgroundColor: resolveColor(styles.bgColor),
                        color: resolveColor(styles.textColor),
                        borderRadius: `${styles.borderRadius}px`,
                        wordWrap: "break-word",
                        padding: `${styles.paddingY}px ${styles.paddingX}px`,
                        fontSize: `${styles.fontSize}px`,
                        fontWeight: styles.fontWeight,
                        lineHeight: styles.lineHeight
                      },
                      children: [
                        hasUiMessageParts ? /* @__PURE__ */ jsx(
                          ConciergeMessagePart,
                          {
                            parts: uiMessageParts,
                            onProductClick: onVariantSelect,
                            isStreaming,
                            pdpStacklineSku,
                            isOnChatWidget,
                            apiBaseUrl
                          }
                        ) : /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "message-markdown",
                            style: { display: "flex", flexDirection: "column" },
                            dangerouslySetInnerHTML: { __html: marked.parse(content) }
                          }
                        ),
                        orders && orders.length > 0 && /* @__PURE__ */ jsx(OrderCardList, { orders })
                      ]
                    }
                  ),
                  bottomRef && /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        height: "1px",
                        width: "100%"
                      },
                      ref: bottomRef
                    }
                  ),
                  !isTyping && !isStreaming && content.length > 0 ? /* @__PURE__ */ jsxs(Actions, { className: "tw:mt-2", children: [
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Copy",
                        tooltip: copied ? "Response copied!" : "Copy response",
                        tooltipOpen: copied || void 0,
                        onClick: handleCopy,
                        children: /* @__PURE__ */ jsx(CopyIcon, {})
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Like",
                        tooltip: liked ? "Thanks for your feedback!" : "Good response",
                        onClick: handleLike,
                        className: liked ? "tw:bg-[#DCDDDC]" : "",
                        children: /* @__PURE__ */ jsx(ThumbsUpIcon, { className: liked ? "tw:fill-current" : "" })
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Dislike",
                        tooltip: disliked ? "Thanks for your feedback!" : "Bad response",
                        onClick: handleDislike,
                        className: disliked ? "tw:bg-[#DCDDDC]" : "",
                        children: /* @__PURE__ */ jsx(ThumbsDownIcon, { className: disliked ? "tw:fill-current" : "" })
                      }
                    ),
                    !isSharedView && /* @__PURE__ */ jsx(Action, { label: "Share", tooltip: "Share", onClick: handleShare, children: /* @__PURE__ */ jsx(ShareIcon, {}) }),
                    isLastMessage && !isSharedView && onRetry && /* @__PURE__ */ jsx(Action, { label: "Retry", tooltip: "Try again", onClick: () => onRetry(), children: /* @__PURE__ */ jsx(RetryIcon, {}) })
                  ] }) : null,
                  followUpsLoading && /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        padding: "4px 0 8px",
                        color: "var(--color-text-secondary, #9ca3af)",
                        fontSize: "13px",
                        letterSpacing: "0.1em"
                      },
                      children: "..."
                    }
                  ),
                  !isTyping && !isStreaming && followUpQueries.length > 0 && !(nextUserMessage && !selectedFollowUp) && /* @__PURE__ */ jsx(FollowUpQueriesWidget, { widgetId: "concierge-follow-up-queries", displayName: "Follow-up Suggestions", children: (widgetStyles, resolveColor2) => {
                    const isDisabled = !isLastMessage || !!selectedFollowUp;
                    const hex = (c) => !c || c.startsWith("#") || c.startsWith("rgb") ? c : `#${c}`;
                    const defaultBg = hex(resolveColor2(widgetStyles.pillBgColor));
                    const defaultText = hex(resolveColor2(widgetStyles.pillTextColor));
                    const defaultBorder = hex(resolveColor2(widgetStyles.pillBorderColor));
                    const hoverBg = defaultText;
                    const hoverText = defaultBg;
                    const hoverBorder = defaultText;
                    const selectedBg = hex(resolveColor2(widgetStyles.pillBgColorSelected));
                    const selectedText = hex(resolveColor2(widgetStyles.pillTextColorSelected));
                    const selectedBorder = hex(resolveColor2(widgetStyles.pillBorderColorSelected));
                    return /* @__PURE__ */ jsxs(
                      "div",
                      {
                        className: "fuq-wrap",
                        style: {
                          display: "flex",
                          flexWrap: "wrap",
                          marginTop: 16,
                          gap: `${widgetStyles.containerGap}px`,
                          width: "100%"
                        },
                        children: [
                          /* @__PURE__ */ jsx("style", { children: `
                          .fuq-wrap .fuq-pill {
                            background-color: ${defaultBg};
                            color: ${defaultText};
                            border-color: ${defaultBorder};
                            transition: all 0.2s ease;
                          }
                          .fuq-wrap .fuq-pill:not(:disabled):not(.fuq-selected):hover {
                            background-color: ${hoverBg};
                            color: ${hoverText};
                            border-color: ${hoverBorder};
                          }
                          .fuq-wrap .fuq-pill.fuq-selected {
                            background-color: ${selectedBg};
                            color: ${selectedText};
                            border-color: ${selectedBorder};
                          }
                        ` }),
                          followUpQueries.map((query, index) => {
                            const isSelected = selectedFollowUp === query;
                            return /* @__PURE__ */ jsx(
                              "button",
                              {
                                className: `fuq-pill${isSelected ? " fuq-selected" : ""}`,
                                onClick: () => handleFollowUpClick(query),
                                disabled: isDisabled && !isSelected,
                                style: {
                                  padding: `${widgetStyles.pillPaddingY}px ${widgetStyles.pillPaddingX}px`,
                                  borderRadius: `${widgetStyles.pillBorderRadius}px`,
                                  borderWidth: widgetStyles.pillBorderWidth,
                                  borderStyle: "solid",
                                  fontSize: `${widgetStyles.pillFontSize}px`,
                                  fontWeight: widgetStyles.pillFontWeight,
                                  lineHeight: "22px",
                                  textAlign: "left",
                                  cursor: isSelected ? "default" : isDisabled ? "not-allowed" : "pointer",
                                  opacity: !isSelected && isDisabled ? 0.5 : 1
                                },
                                children: query
                              },
                              index
                            );
                          })
                        ]
                      }
                    );
                  } })
                ]
              }
            )
          }
        )
      }
    );
    if (isStreaming && !isTyping && !hasAnyContent) return null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: `message ${role}`,
        style: {
          display: "flex",
          flexDirection: "column",
          width: "100%"
        },
        children: [
          !isUser && devInfo && /* @__PURE__ */ jsx(DevInfoPanel, { devInfo }),
          !isUser && (storeLocator || streamingStoreLocator) && /* @__PURE__ */ jsx(
            StoreLocatorWidget,
            {
              stacklineSkus: ((_a = storeLocator || streamingStoreLocator) == null ? void 0 : _a.stacklineSkus) || [],
              apiUrl: apiBaseUrl
            }
          ),
          !isUser && isTyping && /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Chain of Thought", children: (styles, resolveColor) => /* @__PURE__ */ jsxs("div", { style: { padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px` }, children: [
            /* @__PURE__ */ jsx("style", { children: `@keyframes cc-pulse{0%,100%{transform:scale(1)}50%{transform:scale(0.5)}}` }),
            /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  backgroundColor: resolveColor(styles.thinkingTextColor),
                  borderRadius: "50%",
                  animation: "cc-pulse 1s ease-in-out infinite"
                }
              }
            )
          ] }) }),
          !isUser && shimmerActive && /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Chain of Thought", children: (styles, resolveColor) => styles.thinkingEnabled ? /* @__PURE__ */ jsx("div", { style: { maxWidth: "100%" }, children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "message-content",
              style: {
                maxWidth: "100%",
                backgroundColor: "none",
                color: resolveColor(styles.thinkingTextColor),
                borderRadius: `${styles.thinkingBorderRadius}px`,
                borderBottomLeftRadius: `${styles.thinkingBorderRadiusBottomLeft}px`,
                borderBottomRightRadius: `${styles.thinkingBorderRadius}px`,
                wordWrap: "break-word",
                padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px`
              },
              children: /* @__PURE__ */ jsx(
                Shimmer,
                {
                  as: "p",
                  style: { fontSize: `${styles.thinkingTextFontSize}px`, fontFamily: "inherit" },
                  duration: styles.shimmerDuration,
                  color: resolveColor(styles.thinkingTextColor),
                  highlightColor: resolveColor(styles.shimmerColor),
                  children: transientStatus && transientStatus !== "thinking" ? transientStatus : styles.thinkingText
                }
              )
            }
          ) }) : null }),
          isUser && hasUiMessageParts && (() => {
            const imageFiles = uiMessageParts.filter(
              (p) => {
                var _a2;
                return p.type === "file" && ((_a2 = p.mediaType) == null ? void 0 : _a2.startsWith("image/"));
              }
            );
            if (imageFiles.length === 0) return null;
            return /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                  width: "100%",
                  marginBottom: "0.5rem"
                },
                children: imageFiles.map((fp, i) => /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: fp.url,
                    alt: fp.filename || "Uploaded image",
                    style: {
                      maxWidth: imageFiles.length === 1 ? "70%" : "45%",
                      maxHeight: "300px",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: "12px"
                    }
                  },
                  i
                ))
              }
            );
          })(),
          !isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble())
        ]
      }
    );
  }
);
function uiPartsFingerprint(parts) {
  if (!(parts == null ? void 0 : parts.length)) return "";
  return `${parts.length}:${parts.map((p) => {
    var _a, _b;
    if (p.type === "text") return `text|${((_a = p.text) == null ? void 0 : _a.length) ?? 0}`;
    if (p.type === "data-product-carousels" || p.type === "data-section") return `ds|${((_b = p.data) == null ? void 0 : _b.title) ?? ""}`;
    return `${p.type}|${p.state ?? ""}`;
  }).join(",")}`;
}
const ChatMessage = React__default.memo(ChatMessageComponent, (prevProps, nextProps) => {
  var _a, _b;
  return prevProps.role === nextProps.role && prevProps.content === nextProps.content && prevProps.isTyping === nextProps.isTyping && prevProps.bottomRef === nextProps.bottomRef && prevProps.isStreaming === nextProps.isStreaming && prevProps.isLastMessage === nextProps.isLastMessage && ((_a = prevProps.followUpQueries) == null ? void 0 : _a.length) === ((_b = nextProps.followUpQueries) == null ? void 0 : _b.length) && prevProps.nextUserMessage === nextProps.nextUserMessage && prevProps.storeLocator === nextProps.storeLocator && prevProps.streamingStoreLocator === nextProps.streamingStoreLocator && prevProps.orders === nextProps.orders && prevProps.onSearch === nextProps.onSearch && prevProps.onVariantSelect === nextProps.onVariantSelect && prevProps.onSuggestionClick === nextProps.onSuggestionClick && uiPartsFingerprint(prevProps.uiMessageParts) === uiPartsFingerprint(nextProps.uiMessageParts);
});
export {
  ChatMessage
};
//# sourceMappingURL=ChatMessage.js.map
